<script setup>
import { ref, computed, watch } from 'vue'
import api from '@/services/api'
import { CLAUSULAS_COM_VISIB } from '@/constants/CLAUSULAS_COM_VISIB'
import { ST } from '@/constants/ST'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import ClausulasModal from './ClausulasModal.vue'

const props = defineProps({
  idDocumento: { type: Number, required: true },
  tipoDocumento: { type: String, required: true, validator: (v) => ['PO', 'PV', 'OC'].includes(v) },
  lstAplicacaoClausula: { type: String, required: true },
  idsProdutos: { type: Array, default: () => [] },
  idsParceiros: { type: Array, default: () => [] },
  corHeader: { type: String, default: '' },
  corHeaderClass: { type: String, default: 'bg-info-subtle' },
  podeEditar: { type: Boolean, default: true },
  readOnly: { type: Boolean, default: false },
  // === ACCORDION (colapso do card) ===
  /// <summary>Quando true, habilita o comportamento de accordion (header clicável para colapsar/expandir).</summary>
  comAccordion: { type: Boolean, default: false },
  /// <summary>Estado do accordion (aberto/fechado). Usar com v-model para controle bidirecional.</summary>
  accordionAberto: { type: Boolean, default: true },
  /// <summary>Indica se as cláusulas já foram inicializadas (persistido no backend). Quando fornecido, substitui a inferência local.</summary>
  clausulasInicializadas: { type: Boolean, default: null }
})

const emit = defineEmits(['show-notification', 'update:accordionAberto'])

// ============================================================
// MAPEAMENTO DE ENDPOINTS POR TIPO DE DOCUMENTO
// ============================================================
const endpoints = {
  PO: {
    listarVinculadas: (id) => `/PurchaseOrderClausulas/por-po/${id}`,
    listarRascunhos: (id) => `/PurchaseOrders/${id}/clausulas-sugeridas`,
    inicializar: (id) => `/PurchaseOrders/${id}/inicializar-clausulas`,
    recarregar: (id) => `/PurchaseOrders/${id}/recarregar-clausulas`,
    criar: '/PurchaseOrderClausulas',
    atualizarComTemplate: (id) => `/PurchaseOrderClausulas/${id}/atualizar-com-template`,
    atualizarAvulsa: (id) => `/PurchaseOrderClausulas/${id}`,
    deletar: (id) => `/PurchaseOrderClausulas/${id}`,
    tornarDefinitiva: (id) => `/PurchaseOrderClausulas/${id}/tornar-definitiva`,
    sincronizar: (id) => `/PurchaseOrders/${id}/sincronizar-clausulas`
  },
  PV: {
    listarVinculadas: (id) => `/PedidoVendaClausulas/por-pedido-venda/${id}`,
    listarRascunhos: (id) => `/PedidoVendas/${id}/clausulas-sugeridas`,
    inicializar: (id) => `/PedidoVendas/${id}/inicializar-clausulas`,
    recarregar: (id) => `/PedidoVendas/${id}/recarregar-clausulas`,
    criar: (_id) => '/PedidoVendaClausulas',
    criarComOrigem: (_id) => '/PedidoVendaClausulas/criar-com-origem',
    atualizarComTemplate: null,
    atualizarAvulsa: (id) => `/PedidoVendaClausulas/${id}`,
    deletar: (id) => `/PedidoVendaClausulas/${id}`,
    tornarDefinitiva: null,
    sincronizar: (id) => `/PedidoVendas/${id}/sincronizar-clausulas`
  },
  OC: {
    listarVinculadas: (id) => `/OrdemCompraClausulas/por-ordem-compra/${id}`,
    listarRascunhos: (id) => `/OrdensCompra/${id}/clausulas-sugeridas`,
    inicializar: (id) => `/OrdensCompra/${id}/inicializar-clausulas`,
    recarregar: (id) => `/OrdensCompra/${id}/recarregar-clausulas`,
    criar: '/OrdemCompraClausulas',
    atualizarComTemplate: null,
    atualizarAvulsa: (id) => `/OrdemCompraClausulas/${id}`,
    deletar: (id) => `/OrdemCompraClausulas/${id}`,
    tornarDefinitiva: null,
    sincronizar: null
  }
}

const ep = computed(() => endpoints[props.tipoDocumento])

// ============================================================
// ESTADO REATIVO
// ============================================================
const clausulasGridA = ref([]) // Rascunhos (apenas PO)
const clausulasGridB = ref([]) // Vinculadas
const isLoadingClausulas = ref(false)
const clausulasInicializadas = ref(false)
const mostrarModalClausula = ref(false)
const clausulaEditando = ref(null)
const recarregarClausulasConfirmRef = ref(null)

const colunasGridA = [
  { field: 'titulo', label: 'Título', width: 200 },
  { field: 'texto', label: 'Texto', width: 300, formatter: v => v?.substring(0, 100) + '...' },
  { field: 'acoes', label: '', width: 80, slotName: 'acoes' }
]

// ============================================================
// COMPUTED: Produtos e Parceiros disponíveis para o modal
// ============================================================
const produtosDisponiveisParaClausula = computed(() =>
  props.idsProdutos.map(p => {
    if (typeof p === 'number') return { id: p, label: `Produto #${p}` }
    return { id: p.idProduto, label: p.sku || p.nomeProduto || `Produto #${p.idProduto}` }
  })
)

const parceirosDisponiveisParaClausula = computed(() =>
  props.idsParceiros.map(p => {
    if (typeof p === 'number') return { id: p, label: `Parceiro #${p}` }
    const nome = p.nomeParceiro || p.nomeFornecedor || p.nomeCliente || 'Parceiro'
    return { id: p.idParceiro, label: `${nome} #${p.idParceiro}` }
  })
)

// ============================================================
// BADGE DE ORIGEM
// ============================================================
const badgeOrigem = (origem) => {
  const mapa = {
    Empresa: { classes: 'badge badge-ghost-warning', icone: 'bi-building' },
    Produto: { classes: 'badge badge-ghost-danger', icone: 'bi-box' },
    Fornecedor: { classes: 'badge badge-ghost-primary', icone: 'bi-buildings' },
    'Produto Do Fornecedor': { classes: 'badge badge-ghost-success', icone: 'bi-box-seam' },
    Cliente: { classes: 'badge badge-ghost-info', icone: 'bi-people' },
    'Produto Do Cliente': { classes: 'badge badge-ghost-info', icone: 'bi-people' },
    PO: { classes: 'badge badge-ghost-secondary', icone: 'bi-file-text' },
    PV: { classes: 'badge badge-ghost-secondary', icone: 'bi-file-text' },
    OC: { classes: 'badge badge-ghost-secondary', icone: 'bi-file-text' }
  }
  return mapa[origem] || { classes: 'badge badge-ghost-secondary', icone: 'bi-file-text' }
}

// ============================================================
// BADGE DE VISIBILIDADE (com tooltip)
// ============================================================
const badgeVisibilidade = (visib) => {
  const mapa = {
    [CLAUSULAS_COM_VISIB.Interna]: {
      classes: 'badge-ghost-secondary',
      texto: 'Interna',
      tooltip: 'Cláusula de uso interno — não é exibida em documentos externos'
    },
    [CLAUSULAS_COM_VISIB.Externa]: {
      classes: 'badge-ghost-info',
      texto: 'Externa',
      tooltip: 'Cláusula exibida em documentos e relatórios de parceiros'
    },
    [CLAUSULAS_COM_VISIB.Ambas]: {
      classes: 'badge-ghost-primary',
      texto: 'Interno e Externa',
      tooltip: 'Cláusula exibida tanto internamente quanto em documentos externos'
    }
  }
  return mapa[visib] || null
}

// ============================================================
// COR DO HEADER
// ============================================================
const corHeaderStyle = computed(() => {
  if (props.corHeader) {
    return { backgroundColor: props.corHeader }
  }
  return {}
})

const corHeaderClass = computed(() => {
  if (props.corHeader) return ''
  return props.corHeaderClass
})

// ============================================================
// CARREGAR CLÁUSULAS VINCULADAS (Grid B)
// ============================================================
const carregarVinculadas = async () => {
  if (!props.idDocumento) return
  isLoadingClausulas.value = true
  try {
    const resp = await api.get(ep.value.listarVinculadas(props.idDocumento))
    // Filtro fixo: apenas cláusulas ativas
    clausulasGridB.value = (resp.data || []).filter(c =>
      !c.lstStatusClausula || c.lstStatusClausula === ST.Ativo
    )
    // Estado de inicialização é guiado pela prop do backend, não pela presença de vinculadas
    if (props.clausulasInicializadas !== null) {
      clausulasInicializadas.value = props.clausulasInicializadas
    }
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao carregar cláusulas vinculadas.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingClausulas.value = false
  }
}

// ============================================================
// CARREGAR RASCUNHOS (Grid A) — apenas PO
// ============================================================
const carregarRascunhos = async () => {
  if (!props.idDocumento || !ep.value.listarRascunhos) return
  isLoadingClausulas.value = true
  try {
    const resp = await api.get(ep.value.listarRascunhos(props.idDocumento))
    const chaves = montarChavesGridB()
    // Filtro fixo: apenas cláusulas ativas
    const dadosFiltrados = (resp.data || []).filter(c =>
      !c.lstStatusClausula || c.lstStatusClausula === ST.Ativo
    )
    clausulasGridA.value = dadosFiltrados.filter(s => {
      if (s.tipoOrigem === 'Empresa') return !chaves.empresa.has(s.idOrigem)
      if (s.tipoOrigem === 'Produto') return !chaves.produto.has(s.idOrigem)
      if (s.tipoOrigem === 'Fornecedor') return !chaves.fornecedor.has(s.idOrigem)
      if (s.tipoOrigem === 'Produto Do Fornecedor') return !chaves.produtoDoFornecedor.has(s.idOrigem)
      if (s.tipoOrigem === 'Cliente') return !chaves.fornecedor.has(s.idOrigem)
      if (s.tipoOrigem === 'Produto Do Cliente') return !chaves.produtoDoCliente.has(s.idOrigem)
      return true
    })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao carregar rascunhos.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingClausulas.value = false
  }
}

// ============================================================
// MONTAR CHAVES DO GRID B (para filtrar Grid A)
// ============================================================
const montarChavesGridB = () => {
  const empresa = new Set()
  const produto = new Set()
  const fornecedor = new Set()
  const produtoDoFornecedor = new Set()
  const produtoDoCliente = new Set()
  clausulasGridB.value.forEach(c => {
    if (c.idClausulasComerciais != null) empresa.add(c.idClausulasComerciais)
    if (c.idProdutoClausulasComerciais != null) produto.add(c.idProdutoClausulasComerciais)
    if (c.idParceiroClausula != null) fornecedor.add(c.idParceiroClausula)
    if (c.idProdutoFornecedorClausula != null) produtoDoFornecedor.add(c.idProdutoFornecedorClausula)
    if (c.idProdutoClienteClausula != null) produtoDoCliente.add(c.idProdutoClienteClausula)
  })
  return { empresa, produto, fornecedor, produtoDoFornecedor, produtoDoCliente }
}

// ============================================================
// CARREGAR AMBOS OS GRIDS
// ============================================================
const carregarClausulas = async () => {
  await carregarVinculadas()
  if (ep.value.listarRascunhos) {
    await carregarRascunhos()
  }
}

// ============================================================
// INICIALIZAR / RECARREGAR CLÁUSULAS (apenas PO)
// ============================================================
const inicializarOuRecarregarClausulas = async () => {
  if (clausulasInicializadas.value) {
    recarregarClausulasConfirmRef.value?.show()
    return
  }
  await executarInicializarClausulas()
}

const confirmarRecarregarClausulas = async (confirmado) => {
  if (!confirmado) return
  isLoadingClausulas.value = true
  try {
    await api.post(ep.value.recarregar(props.idDocumento))
    await carregarClausulas()
    emit('show-notification', { message: 'Cláusulas recarregadas dos templates.', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao recarregar cláusulas.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingClausulas.value = false
  }
}

const executarInicializarClausulas = async () => {
  isLoadingClausulas.value = true
  try {
    await api.post(ep.value.inicializar(props.idDocumento))
    clausulasInicializadas.value = true
    await carregarClausulas()
    emit('show-notification', { message: 'Cláusulas inicializadas.', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao inicializar cláusulas.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingClausulas.value = false
  }
}

// ============================================================
// UTILIZAR CLÁUSULA (rascunho → vinculada) — apenas PO
// ============================================================
const utilizarClausula = async (sugestao) => {
  try {
    if (props.tipoDocumento === 'PO') {
      await api.post(ep.value.criar, {
        idPurchaseOrder: props.idDocumento,
        idParceiroClausula: sugestao.tipoOrigem === 'Fornecedor' ? sugestao.idOrigem : null,
        idProdutoFornecedorClausula: sugestao.tipoOrigem === 'Produto Do Fornecedor' ? sugestao.idOrigem : null,
        idClausulasComerciais: sugestao.tipoOrigem === 'Empresa' ? sugestao.idOrigem : null,
        idProdutoClausulasComerciais: sugestao.tipoOrigem === 'Produto' ? sugestao.idOrigem : null,
        titulo: sugestao.titulo,
        texto: sugestao.texto,
        ordemExibicao: clausulasGridB.value.length + 1
      })
    } else if (props.tipoDocumento === 'PV' && ep.value.criarComOrigem) {
      await api.post(ep.value.criarComOrigem(props.idDocumento), {
        idPedidoVenda: props.idDocumento,
        titulo: sugestao.titulo,
        texto: sugestao.texto,
        origem: sugestao.tipoOrigem,
        idProduto: (sugestao.tipoOrigem === 'Produto' || sugestao.tipoOrigem === 'Produto Do Cliente') ? sugestao.idOrigem : null,
        idParceiro: sugestao.tipoOrigem === 'Cliente' ? sugestao.idOrigem : null,
        isDefinitiva: sugestao.isDefinitiva
      })
    } else if (props.tipoDocumento === 'OC') {
      // OC: vincula o rascunho/sugestão como cláusula do documento
      await api.post(ep.value.criar, {
        idOrdemCompra: props.idDocumento,
        idParceiroClausula: sugestao.tipoOrigem === 'Fornecedor' ? sugestao.idOrigem : null,
        idProdutoFornecedorClausula: sugestao.tipoOrigem === 'Produto Do Fornecedor' ? sugestao.idOrigem : null,
        idClausulasComerciais: sugestao.tipoOrigem === 'Empresa' ? sugestao.idOrigem : null,
        idProdutoClausulasComerciais: sugestao.tipoOrigem === 'Produto' ? sugestao.idOrigem : null,
        titulo: sugestao.titulo,
        texto: sugestao.texto,
        ordemExibicao: clausulasGridB.value.length + 1
      })
    }
    await carregarClausulas()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao utilizar cláusula.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// DESPREZAR TODOS OS RASCUNHOS
// ============================================================
const desprezarTodasRascunhos = () => { clausulasGridA.value = [] }

// ============================================================
// REMOVER CLÁUSULA VINCULADA
// ============================================================
const removerClausula = async (clausula) => {
  try {
    await api.delete(ep.value.deletar(clausula.idPurchaseOrderClausula || clausula.idPedidoVendaClausula || clausula.idOrdemCompraClausula))
    await carregarClausulas()
    emit('show-notification', { message: 'Cláusula removida.', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao remover cláusula.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// TORNAR DEFINITIVA (apenas PO)
// ============================================================
const tornarDefinitivaClausula = async (clausula) => {
  if (!ep.value.tornarDefinitiva) return
  try {
    await api.patch(ep.value.tornarDefinitiva(clausula.idPurchaseOrderClausula))
    emit('show-notification', { message: 'Cláusula tornada fixa para futuras operações.', type: 'success' })
    await carregarClausulas()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao tornar cláusula definitiva.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// MODAL: ABRIR NOVA / EDITAR
// ============================================================
const abrirModalNovaClausula = () => {
  clausulaEditando.value = null
  mostrarModalClausula.value = true
}

const editarClausula = (clausula) => {
  clausulaEditando.value = { ...clausula }
  mostrarModalClausula.value = true
}

// ============================================================
// MODAL: SALVAR (callback do ClausulasModal)
// ============================================================
const onClausulaSalva = async () => {
  await carregarClausulas()
  mostrarModalClausula.value = false
}

// ============================================================
// WATCH: Recarregar quando idDocumento mudar
// ============================================================
watch(() => props.idDocumento, (newId) => {
  if (newId && newId > 0) {
    // Usa a prop do backend quando disponível, senão assume false
    clausulasInicializadas.value = props.clausulasInicializadas !== null
      ? props.clausulasInicializadas
      : false
    clausulasGridA.value = []
    clausulasGridB.value = []
    // Carrega apenas as cláusulas vinculadas. Os rascunhos (Grid A) só são
    // carregados após "Inicializar Cláusulas" (via carregarClausulas),
    // mantendo o frame vazio até a inicialização explícita.
    carregarVinculadas()
  }
}, { immediate: true })

// Watch adicional: se a prop clausulasInicializadas mudar (ex: após salvar OC),
// atualiza o estado local para refletir o valor do backend
watch(() => props.clausulasInicializadas, (val) => {
  if (val !== null && props.idDocumento) {
    clausulasInicializadas.value = val
  }
})

// ============================================================
// ACCORDION (colapso do card)
// ============================================================
const accordionInterno = ref(props.accordionAberto)

watch(() => props.accordionAberto, (val) => {
  accordionInterno.value = val
})

const toggleAccordion = () => {
  if (!props.comAccordion) return
  accordionInterno.value = !accordionInterno.value
  emit('update:accordionAberto', accordionInterno.value)
}

// ============================================================
// EXPOSE: recarregar() para o componente pai
// ============================================================
defineExpose({
  recarregar: carregarClausulas,
  clausulasInicializadas
})
</script>

<template>
  <div>
    <div class="card mb-3">
      <div class="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2"
           :class="[corHeaderClass, { 'cursor-pointer': props.comAccordion }]" :style="corHeaderStyle"
           @click="toggleAccordion">
        <h6 class="mb-0 fw-bold">
          <i class="bi bi-file-text me-2"></i>Cláusulas Vinculadas
          <span v-if="clausulasGridB.length" class="badge bg-success ms-2">{{ clausulasGridB.length }} definidas</span>
          <span v-if="clausulasGridA.length" class="badge bg-warning ms-2">{{ clausulasGridA.length }} rascunhos</span>
        </h6>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Botão Inicializar / Recarregar -->
          <button
            v-if="ep.inicializar && !props.readOnly"
            type="button"
            class="btn btn-sm btn-info"
            :disabled="isLoadingClausulas || !props.podeEditar"
            @click.stop="inicializarOuRecarregarClausulas"
          >
            <i class="bi" :class="clausulasInicializadas ? 'bi-arrow-clockwise' : 'bi-lightning'"></i>
            {{ clausulasInicializadas ? 'Recarregar Cláusulas' : 'Inicializar Cláusulas' }}
          </button>

          <!-- Recarregar Rascunhos -->
          <button
            v-if="clausulasInicializadas && ep.listarRascunhos && !props.readOnly"
            type="button"
            class="btn btn-sm btn-outline-warning"
            :disabled="isLoadingClausulas || !props.podeEditar"
            @click.stop="carregarRascunhos"
            title="Recarrega apenas os rascunhos/sugestões, sem alterar as cláusulas já definidas"
          >
            <i class="bi bi-arrow-repeat"></i> Recarregar Rascunhos
          </button>

          <!-- Nova Cláusula (somente após as cláusulas serem inicializadas) -->
          <button
            v-if="!props.readOnly && clausulasInicializadas"
            type="button"
            class="btn btn-sm btn-outline-success"
            :disabled="!props.podeEditar"
            @click.stop="abrirModalNovaClausula"
          >
            <i class="bi bi-plus-circle"></i> Nova Cláusula
          </button>

          <!-- Desprezar Todas (quando há rascunhos) -->
          <button
            v-if="clausulasGridA.length && !props.readOnly"
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="!props.podeEditar"
            @click.stop="desprezarTodasRascunhos"
          >
            <i class="bi bi-x-circle"></i> Desprezar Todas
          </button>

          <!-- Chevron do accordion -->
          <i
            v-if="props.comAccordion"
            class="bi fs-5"
            :class="accordionInterno ? 'bi-chevron-up' : 'bi-chevron-down'"
          ></i>
        </div>
      </div>

      <div class="card-body" v-show="accordionInterno">
        <!-- Loading -->
        <div v-if="isLoadingClausulas" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-info me-2"></div>
          Carregando cláusulas...
        </div>

        <template v-if="!isLoadingClausulas">
          <!-- PO/PV: Grid A (Rascunhos) + Grid B (Vinculadas) lado a lado (quando há listarRascunhos) -->
          <div v-if="ep.listarRascunhos" class="row">
            <!-- Grid A: Rascunhos -->
            <div class="col-md-5 mb-3">
              <div class="card border-warning h-100" style="opacity: 0.7">
                <div class="card-header bg-warning-subtle py-1">
                  <small class="fw-bold text-warning-emphasis">Rascunhos / Sugestões</small>
                </div>
                <AdminTable
                  :columns="colunasGridA"
                  :data="clausulasGridA"
                  :loading="isLoadingClausulas"
                  empty-message="Nenhum rascunho pendente"
                  row-key="idOrigem"
                  :selectable="false"
                >
                  <template #acoes="{ row }">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-info"
                      :disabled="!props.podeEditar"
                      @click="utilizarClausula(row)"
                    >
                      <i class="bi bi-arrow-right"></i> Utilizar
                    </button>
                  </template>
                </AdminTable>
              </div>
            </div>

            <!-- Grid B: Vinculadas -->
            <div class="col-md-7 mb-3">
              <div class="card border-success h-100">
                <div class="card-header bg-success-subtle py-1">
                  <small class="fw-bold text-success-emphasis">Cláusulas do Documento</small>
                </div>
                <div class="list-group list-group-flush">
                  <div
                    v-for="(row, idx) in clausulasGridB"
                    :key="row.idPurchaseOrderClausula || row.idPedidoVendaClausula || row.idOrdemCompraClausula"
                    class="list-group-item list-group-item-action d-flex align-items-start gap-2 px-3 py-2"
                  >
                    <span class="badge bg-secondary me-1 mt-1">{{ idx + 1 }}</span>
                    <div class="flex-grow-1 min-width-0">
                      <div class="d-flex align-items-center gap-2">
                        <strong class="small text-truncate">{{ row.titulo }}</strong>
                        <span class="badge flex-shrink-0" :class="badgeOrigem(row.origemDescricao || row.origem).classes">
                          <i class="bi me-1" :class="badgeOrigem(row.origemDescricao || row.origem).icone"></i>
                          {{ row.origemDescricao || row.origem }}
                        </span>
                        <span
                          v-if="badgeVisibilidade(row.lstVisibilidade)"
                          class="badge flex-shrink-0"
                          :class="badgeVisibilidade(row.lstVisibilidade).classes"
                          :title="badgeVisibilidade(row.lstVisibilidade).tooltip"
                        >
                          {{ badgeVisibilidade(row.lstVisibilidade).texto }}
                        </span>
                      </div>
                      <small class="text-muted d-block text-truncate">{{ row.texto?.substring(0, 120) }}...</small>
                    </div>
                    <div v-if="!props.readOnly" class="btn-group btn-group-sm flex-shrink-0">
                      <!-- Editar (não disponível para Empresa/Produto em PO) -->
                      <button
                        v-if="props.podeEditar && !(props.tipoDocumento === 'PO' && (row.origemDescricao === 'Empresa' || row.origemDescricao === 'Produto'))"
                        type="button"
                        class="btn btn-outline-primary"
                        @click="editarClausula(row)"
                        title="Editar"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                      <!-- Tornar Definitiva (apenas PO) -->
                      <button
                        v-if="props.tipoDocumento === 'PO' && ep.tornarDefinitiva && row.origemDescricao !== 'PO' && row.origemDescricao !== 'Empresa' && row.origemDescricao !== 'Produto'"
                        type="button"
                        class="btn btn-outline-success"
                        :class="{'opacity-50': row.isDefinitiva}"
                        @click="tornarDefinitivaClausula(row)"
                        :title="row.isDefinitiva ? 'Já é fixa' : 'Tornar cláusula fixa para o fornecedor'"
                      >
                        <i class="bi" :class="row.isDefinitiva ? 'bi-star-fill' : 'bi-star'"></i>
                      </button>
                      <!-- Remover -->
                      <button
                        v-if="props.podeEditar && !row.isFixa"
                        type="button"
                        class="btn btn-outline-danger"
                        @click="removerClausula(row)"
                        title="Remover"
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Empty states -->
                <div v-if="!clausulasGridB.length && clausulasInicializadas" class="text-center py-5 px-3">
                  <div class="mb-3">
                    <i class="bi bi-file-text" style="font-size: 3rem; color: var(--bs-info); opacity: 0.5;"></i>
                  </div>
                  <h6 class="fw-bold text-body-secondary">Nenhuma cláusula cadastrada</h6>
                  <p class="small text-muted mb-3">
                    Não foram encontradas cláusulas nos templates.
                    <br v-if="!props.readOnly">Clique em <strong>"Nova Cláusula"</strong> acima para adicionar manualmente.
                  </p>
                  <button
                    v-if="!props.readOnly"
                    type="button"
                    class="btn btn-sm btn-outline-success"
                    :disabled="!props.podeEditar"
                    @click="abrirModalNovaClausula"
                  >
                    <i class="bi bi-plus-circle"></i> Criar Primeira Cláusula
                  </button>
                </div>
                <div v-if="!clausulasGridB.length && !clausulasInicializadas && ep.inicializar" class="text-center text-muted py-4">
                  <i class="bi bi-info-circle me-2"></i> Clique em "Inicializar Cláusulas" para carregar dos templates.
                </div>
                <div v-if="!clausulasGridB.length && !clausulasInicializadas && !ep.inicializar" class="text-center text-muted py-4">
                  <i class="bi bi-info-circle me-2"></i> Nenhuma cláusula vinculada encontrada.
                </div>
              </div>
            </div>
          </div>

          <!-- OC: Apenas Grid B (Vinculadas) — quando não há listarRascunhos -->
          <div v-else>
            <div class="card border-success">
              <div class="card-header bg-success-subtle py-1">
                <small class="fw-bold text-success-emphasis">Cláusulas do Documento</small>
              </div>
              <div class="list-group list-group-flush">
                <div
                  v-for="(row, idx) in clausulasGridB"
                  :key="row.idPedidoVendaClausula || row.idOrdemCompraClausula"
                  class="list-group-item list-group-item-action d-flex align-items-start gap-2 px-3 py-2"
                >
                  <span class="badge bg-secondary me-1 mt-1">{{ idx + 1 }}</span>
                  <div class="flex-grow-1 min-width-0">
                    <div class="d-flex align-items-center gap-2">
                      <strong class="small text-truncate">{{ row.titulo }}</strong>
                      <span class="badge flex-shrink-0" :class="badgeOrigem(row.origemDescricao || row.origem).classes">
                        <i class="bi me-1" :class="badgeOrigem(row.origemDescricao || row.origem).icone"></i>
                        {{ row.origemDescricao || row.origem }}
                      </span>
                      <span
                        v-if="badgeVisibilidade(row.lstVisibilidade)"
                        class="badge flex-shrink-0"
                        :class="badgeVisibilidade(row.lstVisibilidade).classes"
                        :title="badgeVisibilidade(row.lstVisibilidade).tooltip"
                      >
                        {{ badgeVisibilidade(row.lstVisibilidade).texto }}
                      </span>
                    </div>
                    <small class="text-muted d-block text-truncate">{{ row.texto?.substring(0, 120) }}...</small>
                  </div>
                  <div v-if="!props.readOnly" class="btn-group btn-group-sm flex-shrink-0">
                    <button
                      v-if="props.podeEditar"
                      type="button"
                      class="btn btn-outline-primary"
                      @click="editarClausula(row)"
                      title="Editar"
                    >
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button
                      v-if="props.podeEditar && !row.isFixa"
                      type="button"
                      class="btn btn-outline-danger"
                      @click="removerClausula(row)"
                      title="Remover"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="!clausulasGridB.length" class="text-center py-5 px-3">
                <div class="mb-3">
                  <i class="bi bi-file-text" style="font-size: 3rem; color: var(--bs-info); opacity: 0.5;"></i>
                </div>
                <h6 class="fw-bold text-body-secondary">Nenhuma cláusula cadastrada</h6>
                <p class="small text-muted mb-3">
                  Não foram encontradas cláusulas vinculadas a este documento.
                  <br v-if="!props.readOnly">Clique em <strong>"Nova Cláusula"</strong> acima para adicionar manualmente.
                </p>
                <button
                  v-if="!props.readOnly"
                  type="button"
                  class="btn btn-sm btn-outline-success"
                  :disabled="!props.podeEditar"
                  @click="abrirModalNovaClausula"
                >
                  <i class="bi bi-plus-circle"></i> Criar Primeira Cláusula
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Modal de Cláusula -->
    <ClausulasModal
      v-model="mostrarModalClausula"
      :id-documento="props.idDocumento"
      :tipo-documento="props.tipoDocumento"
      :lst-aplicacao-clausula="props.lstAplicacaoClausula"
      :clausula-editando="clausulaEditando"
      :produtos-disponiveis="produtosDisponiveisParaClausula"
      :parceiros-disponiveis="parceirosDisponiveisParaClausula"
      @salva="onClausulaSalva"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <!-- Confirmação para Recarregar Cláusulas -->
    <ConfirmationModal
      v-if="ep.recarregar"
      ref="recarregarClausulasConfirmRef"
      title="Recarregar Cláusulas?"
      message="Isso irá <strong>remover todas as cláusulas atuais</strong> que vieram dos templates e recarregá-las dos cadastros originais.<br><br>As cláusulas avulsas (criadas manualmente apenas para este documento) <strong>não serão afetadas</strong>.<br><br>Deseja continuar?"
      confirm-button-text="Sim, recarregar"
      cancel-button-text="Cancelar"
      @confirmed="confirmarRecarregarClausulas"
      @close="() => {}"
    />
  </div>
</template>
